// import { queryOptions } from '@tanstack/react-query'
import type { MergeExclusive, Simplify } from 'type-fest'
import { saveToLocalStorage } from './localStorage'
import type { BaseMapDeployment, ServerReceivedMapDeployments } from './models'

type ValidMapDeploymentResult<T extends BaseMapDeployment> = {
	success: true
	data: ServerReceivedMapDeployments<T>
}
type InvalidMapDeploymentResult = {
	success: false
	message: string
}

export type ValidateMapDeploymentsResult<T extends BaseMapDeployment> =
	Simplify<
		MergeExclusive<ValidMapDeploymentResult<T>, InvalidMapDeploymentResult>
	>

type Success<T extends BaseMapDeployment> = {
	success: true
	data: ServerReceivedMapDeployments<T>
}

type Failure = {
	success: false
	message: string
}

type FetchMapDeploymentsResult<T extends BaseMapDeployment> = Simplify<
	MergeExclusive<Success<T>, Failure>
>

type FetchMapDeploymentsOptions<T extends BaseMapDeployment> = {
	localstorageKey: string
	mapDeploymentsFetchUrl: string
	skipValidate?: boolean
	validationFn?: (
		data: ServerReceivedMapDeployments<T>
	) => ValidateMapDeploymentsResult<T>
}

export async function fetchMapDeployments<T extends BaseMapDeployment>({
	localstorageKey,
	mapDeploymentsFetchUrl,
	skipValidate = false,
	validationFn,
}: FetchMapDeploymentsOptions<T>): Promise<FetchMapDeploymentsResult<T>> {
	try {
		const req = await fetch(mapDeploymentsFetchUrl)
		if (!req.ok) {
			throw new Error(
				`- failed to fetch: ${req.status} ${req.statusText}`
			)
		}

		const res = (await req.json()) as ServerReceivedMapDeployments<T>

		// we have the options to skip validation if we want to,
		// but by default we will validate the data from the server before returning it
		if (skipValidate) return { success: true, data: res }
		if (typeof validationFn !== 'function') {
			return {
				success: false,
				message:
					'validationFn is required to validate data from server',
			}
		}
		// validate data before returning it
		const { data, success } = validationFn(res)
		if (success) {
			const savedToLocalStorage = saveToLocalStorage(
				data,
				localstorageKey
			)
			if (!savedToLocalStorage.success) {
				return {
					success: false,
					message: 'failed to save data to local storage',
				}
			}
			return { success: true, data }
		}
		return {
			success: false,
			message: `error validating data from server: `,
		}
	} catch (error) {
		if (error instanceof Error) {
			return {
				success: false,
				message: `error fetching data: ${error.message}`,
			}
		}
	}
	return { success: false, message: 'unknown error fetching data' }
}

// export const mapDeploymentsOptions = queryOptions({
// 	queryKey: ['mapDeployments'],
// 	queryFn: async () => fetchMapDeployments(),
// })
